import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProfileByUsername, getBlocks, recordView } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import PublicProfileRenderer from '../components/PublicProfileRenderer';
import DemoThemeSelector from '../components/DemoThemeSelector';
import SEOHead from '../components/SEOHead';
import { FiUser, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PublicProfilePage = () => {
  const { username } = useParams();
  const { walletAddress } = useAuth();
  const [profile, setProfile] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [demoTheme, setDemoTheme] = useState('dark-pro');

  // Demo profile data
  const demoProfile = {
    id: 'demo-id',
    username: 'demo',
    display_name: 'Alex Web3',
    bio: 'Web3 creator, NFT collector, and blockchain enthusiast. Building the future of decentralized identity. Try different themes using the selector above!',
    avatar: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3e%3cdefs%3e%3clinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' style='stop-color:%2384cc16;stop-opacity:1' /%3e%3cstop offset='100%25' style='stop-color:%2310b981;stop-opacity:1' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='url(%23grad)'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='36' font-weight='bold'%3eAW%3c/text%3e%3c/svg%3e",
    wallet_address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0',
    theme: demoTheme,
    is_public: true,
  };

  const demoBlocks = [
    // Social Links
    {
      id: '1',
      type: 'link',
      data: { 
        title: 'Follow on Twitter', 
        url: 'https://twitter.com', 
        icon: '🐦',
        description: 'Latest updates and thoughts'
      }
    },
    {
      id: '2',
      type: 'link',
      data: { 
        title: 'GitHub Profile', 
        url: 'https://github.com', 
        icon: '💻',
        description: 'Open source projects'
      }
    },
    {
      id: '3',
      type: 'link',
      data: { 
        title: 'LinkedIn', 
        url: 'https://linkedin.com', 
        icon: '💼',
        description: 'Professional network'
      }
    },
    // Text Block
    {
      id: '4',
      type: 'text',
      data: {
        title: 'About Me',
        content: 'I\'m a passionate Web3 developer and creator building tools for the decentralized future. I love exploring new blockchain technologies, creating NFT art, and helping others navigate the crypto space. This HubID profile showcases all the amazing features available!'
      }
    },
    // Image Block
    {
      id: '5',
      type: 'image',
      data: {
        url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450' viewBox='0 0 800 450'%3e%3cdefs%3e%3clinearGradient id='imgGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' style='stop-color:%2384cc16;stop-opacity:1' /%3e%3cstop offset='50%25' style='stop-color:%2310b981;stop-opacity:1' /%3e%3cstop offset='100%25' style='stop-color:%2306b6d4;stop-opacity:1' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='url(%23imgGrad)'/%3e%3ctext x='50%25' y='45%25' fill='white' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='48' font-weight='bold'%3eWeb3 Showcase%3c/text%3e%3ctext x='50%25' y='60%25' fill='rgba(255,255,255,0.9)' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='24'%3eBuilding the Decentralized Future%3c/text%3e%3c/svg%3e",
        title: 'Featured Work',
        description: 'A glimpse into my Web3 journey'
      }
    },
    // Image Gallery
    {
      id: '6',
      type: 'gallery',
      data: {
        images: [
          {
            url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3e%3crect width='100%25' height='100%25' fill='%2384cc16'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-size='24'%3eNFT Art%3c/text%3e%3c/svg%3e",
            alt: 'NFT Collection'
          },
          {
            url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3e%3crect width='100%25' height='100%25' fill='%2310b981'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-size='24'%3eDApp UI%3c/text%3e%3c/svg%3e",
            alt: 'DApp Interface'
          },
          {
            url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3e%3crect width='100%25' height='100%25' fill='%2306b6d4'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-size='24'%3eBlockchain%3c/text%3e%3c/svg%3e",
            alt: 'Blockchain Tech'
          },
          {
            url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3e%3crect width='100%25' height='100%25' fill='%23a855f7'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-size='24'%3eDeFi%3c/text%3e%3c/svg%3e",
            alt: 'DeFi Platform'
          }
        ],
        layout: 'grid'
      }
    },
    // Video Block
    {
      id: '7',
      type: 'video',
      data: {
        title: 'My Latest Video',
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        description: 'Check out my latest Web3 tutorial'
      }
    },
    // Product Block
    {
      id: '8',
      type: 'product',
      data: {
        title: 'Web3 Course Bundle',
        description: 'Complete guide to building on Ethereum, Polygon, and more. Includes 50+ hours of content, code examples, and community access.',
        price: '0.5 ETH',
        url: 'https://example.com/course',
        image: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3e%3crect width='100%25' height='100%25' fill='%2384cc16'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-size='32'%3eWeb3 Course%3c/text%3e%3c/svg%3e",
        cta: 'Enroll Now'
      }
    },
    // Calendar Block
    {
      id: '9',
      type: 'calendar',
      data: {
        title: 'Book a Consultation',
        url: 'https://cal.com/demo',
        description: 'Schedule a 30-minute call to discuss your Web3 project',
        provider: 'cal.com'
      }
    },
    // Tip Jar
    {
      id: '10',
      type: 'tipjar',
      data: {
        enabled: true,
        title: 'Support My Work',
        description: 'If you find my content helpful, consider sending a tip!',
        wallet: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0',
        networks: ['ethereum', 'polygon']
      }
    },
    // NFT Gallery (Coming Soon)
    {
      id: '11',
      type: 'nftgallery',
      data: {
        enabled: true,
        title: 'My NFT Collection',
        description: 'Showcasing my favorite NFTs',
        wallet: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0',
        network: 'ethereum'
      }
    },
    // Token Gated (Coming Soon)
    {
      id: '12',
      type: 'tokengated',
      data: {
        enabled: true,
        title: 'Exclusive Content',
        description: 'This content is only available to holders of my NFT collection',
        contract: '0x1234567890123456789012345678901234567890',
        network: 'ethereum',
        minBalance: 1
      }
    },
  ];

  useEffect(() => {
    if (username === 'demo') {
      // Use demo profile
      setProfile({ ...demoProfile, theme: demoTheme });
      setBlocks(demoBlocks);
      setLoading(false);
      return;
    }
    loadProfile();
  }, [username]);
  
  // Update demo theme separately to avoid reloading
  useEffect(() => {
    if (username === 'demo' && profile && profile.id === 'demo-id') {
      setProfile({ ...profile, theme: demoTheme });
    }
  }, [demoTheme]);
  
  useEffect(() => {
    // Record view after profile loads (skip for demo)
    if (profile && profile.id !== 'demo-id') {
      recordView(profile.id, walletAddress);
    }
  }, [profile, walletAddress]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: profileData, error: profileError } = await getProfileByUsername(username);

      if (profileError || !profileData) {
        setError('Profile not found');
        return;
      }
      
      if (!profileData.is_public) {
        setError('This profile is private');
        return;
      }

      setProfile(profileData);

      // Load blocks
      const { data: blocksData } = await getBlocks(profileData.id);
      setBlocks(blocksData || []);

    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (themeId) => {
    setDemoTheme(themeId);
    if (profile && profile.id === 'demo-id') {
      setProfile({ ...profile, theme: themeId });
    }
  };

  if (loading && username !== 'demo') {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-dark-300">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <SafeIcon icon={FiAlertCircle} className="text-dark-500 text-6xl mb-4 mx-auto" />
          <h1 className="text-2xl font-bold text-white mb-2">Profile Not Found</h1>
          <p className="text-dark-300 mb-6">{error}</p>
          <a
            href="/#/"
            className="inline-flex items-center px-6 py-3 glass-orange text-orange-400 font-medium rounded-lg hover:text-orange-300 transition-colors border border-orange-500/50"
          >
            <SafeIcon icon={FiUser} className="mr-2" />
            Create Your Profile
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead profile={profile} username={username} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen"
      >
        {username === 'demo' && (
          <div className="max-w-4xl mx-auto px-4 pt-8">
            <DemoThemeSelector onThemeChange={handleThemeChange} currentTheme={demoTheme} />
          </div>
        )}
        <PublicProfileRenderer profile={profile} blocks={blocks} />
      </motion.div>
    </>
  );
};

export default PublicProfilePage;